import { getUserEmail } from '@/utils/get-user-email';
import { http } from '../http';
import { Annotation } from '../pages/upload-document/components/annotation/annotation';
import {
	LineageReason,
	PublicRetrievalReason,
} from '@/entities/document-lineage';

type Signer = {
	order: number;
	email: string;
	role: 'SIGNER';
	components: Omit<Annotation, 'email' | 'id'>[];
};

export type VisibleSignatureOptions = {
	page_number: number;
	position_x: number;
	position_y: number;
	width: number;
	height: number;
	include_date: boolean;
	include_initials: boolean;
	include_motive: boolean;
	include_signature_graphic: boolean;
	motive?: string;
};

export type SignUploadedDocumentResponse = {
	documentUlid: string;
	signedPath: string;
	success: boolean;
	// Lineage page (see src/entities/document-lineage.ts). Absent on backends
	// that predate the feature, which is equivalent to "no page was added".
	lineage_added?: boolean;
	lineage_reason?: LineageReason | string | null;
	verify_url?: string | null;
	// Path 2. Always present on backends that support public retrieval; absent
	// on older ones, which is equivalent to "not enabled".
	public_retrieval?: boolean;
	public_reason?: PublicRetrievalReason | string | null;
};

export async function signUploadedDocument(
	documentUlid: string,
	fileName: string,
	certificateId: string,
	otp: string,
	annotations?: Annotation[],
	otpType: 'sms' | 'pin' = 'sms',
	visibleSignatureOptions?: VisibleSignatureOptions[],
	lineagePage: boolean = false,
	publicRetrieval: boolean = false,
) {
	const signers: Signer[] | undefined = annotations
		?.map((annotation) => annotation.email)
		.filter((email, index, array) => !array.slice(0, index).includes(email))
		.filter((email) => email !== getUserEmail() && email !== 'Yo')
		.map((signerEmail, index) => ({
			order: index + 1,
			email: signerEmail,
			role: 'SIGNER',
			components: annotations.filter(
				(annotation) => annotation.email === signerEmail,
			),
		}));

	const signUploadedDocumentResponse =
		await http.post<SignUploadedDocumentResponse>(
			`documents/${documentUlid}/sign`,
			{
				file_name: fileName,
				certificate_id: certificateId,
				signature_format: 'PKCS7',
				otp: {
					value: otp,
					type: otpType,
				},
				signers,
				lineage_page: lineagePage,
				// Public retrieval only exists on top of the lineage page: the
				// QR on that page is how an anonymous holder reaches the file.
				public_retrieval: lineagePage && publicRetrieval,
				...(visibleSignatureOptions?.length
					? { visible_signature_options: visibleSignatureOptions }
					: {}),
			},
		);

	return signUploadedDocumentResponse;
}
